import { useState, useRef, useLayoutEffect } from "react";
import { Box, Typography, Divider, Button } from "src/components";
import UserDetailsScreen from "./user-details-screen";
import UserVehicleScreen from "./user-vehicle-screen";
import SuccessScreen from "./success-screen";
import TutorialScreens from "./tutorial-screens";
import { useSnackbar, useIsMobile } from 'src/hooks';
import { useAuth, updateProfile, updateUserImages } from "src/utils";

const stepTitles = ["Your Details", "Your Vehicle", "Profile Complete!"];

import tutorial1 from 'src/assets/videos/onboarding/tutorial1.mp4';
import tutorial2 from 'src/assets/videos/onboarding/tutorial2.mp4';
import tutorial3 from 'src/assets/videos/onboarding/tutorial3.mp4';
import tutorial4 from 'src/assets/videos/onboarding/tutorial4.mp4';

export const tutorialScreensData = [
  { mp4: tutorial1, message: "Track & share your route through building a digital adventure log." },
  { mp4: tutorial2, message: "Build community & connect with other travellers on the road." },
  { mp4: tutorial3, message: "Everything you need to know about the Pan-American Highway in one place!" },
  { mp4: tutorial4, message: "Discover what not to miss with our pinned map feature!" },
];

export default function OnboardingContainer({ scrollRef, handleCurrentViewChange }) {
    const { user, fetchUser } = useAuth();
    const [step, setStep] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [inTutorial, setInTutorial] = useState(false);
    const { openSnackbar } = useSnackbar();
    const isMobile = useIsMobile();
    const contentRef = useRef(null);

    const [form, setForm] = useState({
        name: user.name || "",
        countryIsoAlpha2: user.countryIsoAlpha2 || "",
        bio: user.bio || "",
        transportMode: user.transportMode || "Car",
        profileImage: user.profileImageUrl || null,
        previewProfileImage: user.profileImageUrl || null,
        vehicleImage: user.vehicleImageUrl || null,
        previewVehicleImage: user.vehicleImageUrl || null,
        instagramHandle: user.instagramHandle || "",
        socialMediaLinks: user.socialMediaLinks?.length ? user.socialMediaLinks : [""],
    });

    const steps = [
        () => <UserDetailsScreen form={form} setForm={setForm} scrollRef={scrollRef} />,
        () => <UserVehicleScreen form={form} setForm={setForm} />,
        () => <SuccessScreen />,
    ];

    const goNext = () => {
        if (inTutorial) {
        if (step === tutorialScreensData.length - 1) {
            handleCurrentViewChange("AccountSummary");
        } else {
            setStep((s) => s + 1);
        }
        } else {
        setStep((s) => Math.min(s + 1, steps.length - 1));
        }
    };

    const finishTutorial = async () => {
        try {
            setIsSaving(true);
            await updateProfile({ hasCompletedOnboarding: true });
            await fetchUser();
            openSnackbar("Onboarding complete!", "success");
        } catch (error) {
            console.error("Failed to finalize onboarding", error);
            openSnackbar("Failed to complete onboarding", "error");
        } finally {
            setIsSaving(false);
            handleCurrentViewChange("AccountSummary");
        }
        };

    const handleGetStarted = async () => {
        setIsSaving(true);
        try {
            let uploadedUrls = {};
            if ((form.profileImage && form.profileImage !== user.profileImageUrl) ||
                (form.vehicleImage && form.vehicleImage !== user.vehicleImageUrl)) {
                uploadedUrls = await updateUserImages(form.profileImage, form.vehicleImage);
            }
            const payload = {
                name: form.name,
                bio: form.bio,
                countryIsoAlpha2: form.countryIsoAlpha2,
                instagramHandle: form.instagramHandle,
                socialMediaLinks: form.socialMediaLinks.filter(Boolean),
                transportMode: form.transportMode,
                profileImageUrl: uploadedUrls.profileImageUrl || form.profileImage, 
                vehicleImageUrl: uploadedUrls.vehicleImageUrl || form.vehicleImage
            };
            await updateProfile(payload);
        } catch (error) {
            console.error("Profile update failed", error);
            openSnackbar("Profile update failed", "error");
        } finally {
            setIsSaving(false);
            setInTutorial(true); 
            setStep(0); 
        }
    };

    const CurrentScreen = inTutorial ? (
        <TutorialScreens
            data={tutorialScreensData}
            step={step}
        />
        ) : (
        steps[step]()
    );

    useLayoutEffect(() => {
        if (scrollRef?.current) {
            scrollRef.current.scrollTo({ top: 0, behavior: 'auto' });
        }
    }, [step, inTutorial, scrollRef]);

    return (
        <Box ref={contentRef} sx={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>
            {/* Header */}
            {!inTutorial && (<>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    bgcolor="#fff"
                >
                    <Typography variant="h6" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                        {stepTitles[step]}
                    </Typography>
                </Box> 
                
                <Divider mb={1} />
            </>)}           

            {/* Content */}
            <Box
                display="flex"
                flexDirection="column"
                alignItems="stretch"
                justifyContent="flex-start"
                sx={{ 
                    flexGrow: 1, 
                    px: 2,
                    ...(inTutorial ? {height: 'calc(100% - 80px)'} : {}) }}
            >
                {CurrentScreen}
            </Box>

            {/* Footer buttons */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    bgcolor: "#fff",
                    pt: 1,
                    pb: isMobile ? 2 : 0,
                    width: "100%",
                    px: 2,
                    borderRadius: "12px",
                }}
            >
                {!inTutorial && step < steps.length - 1 && (
                    <Button 
                        variant="contained" 
                        fullWidth
                        disabled={!form.name} 
                        onClick={goNext}>
                        Continue
                    </Button>
                )}

                {!inTutorial && step === steps.length - 1 && (
                    <Button variant="contained" fullWidth onClick={handleGetStarted} disabled={isSaving}>
                        {isSaving ? "Saving..." : "Get Started"}
                    </Button>
                )}

                {inTutorial && (
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={(e) => {
                            e.currentTarget.blur(); 
                            if (step === tutorialScreensData.length - 1) {
                                finishTutorial();
                            } else {
                                setStep((s) => s + 1);
                            }
                        }}
                        >
                        {step === tutorialScreensData.length - 1 ? "Finish" : "Next"}
                    </Button>
                )}

            </Box>
        </Box>
    );
}
